// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SectionWrapperStyles } from "@adl/App/Components/Common/SectionWrapper";
import { color, xScreen } from "@adl/XDL/Utilities";
import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import HelpCircle from "@insite/mobius/Icons/HelpCircle";
import { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export const securityCodeHelpLink: LinkPresentationProps = {
    icon: {
        iconProps: {
            src: HelpCircle,
            size: 12,
            color: "common.ui6",
            css: css`
                margin-right: 0;
            `,
        },
    },
};

export const securityCodeTextInput: TextFieldPresentationProps = {
    cssOverrides: {
        formField: css`
            max-width: 70px;
            position: relative;
        `,
        formInputWrapper: css`
            display: flex;
            align-items: flex-end;
            flex-direction: column;
        `,
    },
};

interface CheckoutPageCartTotalStyles {
    container?: GridContainerProps;
    buttonsGridItem?: GridItemProps;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
    continueButton?: ButtonPresentationProps;
}

export const checkoutPageCartTotalStyles: CheckoutPageCartTotalStyles = {
    container: {
        gap: 0,
        css: css`
            border: 1px solid ${color("common.border")};
            border-radius: 2px;
            background-color: ${color("common.white")};
            margin-bottom: 22px;
        `,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            padding: 0 16px 16px;
        `,
    },
    cartTotalGridItem: {
        width: 12,
    },
    cartTotal: {
        container: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding-bottom: 16px;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
        labelGridItem: {
            css: css`
                padding-bottom: 0;
            `,
        },
        valueGridItem: {
            css: css`
                padding-bottom: 0;
            `,
        },
    },
    continueButton: {
        variant: "primary",
        sizeVariant: "large",
        css: css`
            width: 100%;
            margin-top: 22px;
        `,
    },
};

export const checkoutPageSectionWrapperStyles: SectionWrapperStyles = {
    wrapper: {
        css: css`
            ${xScreen("lte", "md")} {
                &.addresses-section > div {
                    margin-bottom: 20px;
                }
            }
        `,
    },
    title: {
        wrapper: {
            css: css`
                justify-content: space-between;
                ${xScreen("lte", "md")} {
                    flex-direction: column;
                    align-items: flex-start;
                    margin-bottom: 40px;
                    & > .available-cards-container {
                        margin-top: 20px;
                        margin-bottom: 0;
                    }
                }
            `,
        },
    },
};

export interface CheckoutReviewSectionStyles {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    sectionWrapper?: SectionWrapperStyles;
    itemlabel?: TypographyProps;
    itemValue?: TypographyProps;
    addressesAndShippingContainer?: GridContainerProps;
    addressesAndShippingGridItem?: GridItemProps;
    addressesContainer?: InjectableCss;
    shippingContainer?: InjectableCss;
    paymentContainer?: GridContainerProps;
    paymentGridItem?: GridItemProps;
    poGridItem?: GridItemProps;
    jobGridItem?: GridItemProps;
    itemContainer?: InjectableCss;
}

const sharedReviewSectionStyles: CheckoutReviewSectionStyles = {
    container: {
        gap: 0,
    },
    gridItem: {
        width: 12,
    },
    itemlabel: {
        weight: "bold",
        css: css`
            margin-right: 8px;
            white-space: nowrap;
        `,
    },
    itemContainer: {
        css: css`
            display: flex;
            margin-bottom: 12px;
        `,
    },
    sectionWrapper: {
        title: {
            wrapper: {
                css: css`
                    justify-content: space-between;
                    margin-bottom: 12px;
                    &&& {
                        align-items: center;
                    }
                    ${xScreen("lte", "sm")} {
                        flex-direction: row;
                    }
                `,
            },
            title: {
                variant: "h3",
                color: "primary",
            },
        },
    },
    addressesContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-right: 22px;
        `,
    },
};

export const checkoutReviewShippingSectionStyles: CheckoutReviewSectionStyles = {
    ...sharedReviewSectionStyles,
    addressesAndShippingContainer: {
        gap: 88,
        css: css`
            margin-bottom: 36px;
        `,
    },
    addressesAndShippingGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            ${xScreen("lte", "sm")} {
                padding-top: 6px;
                padding-bottom: 6px;
            }
        `,
    },
    shippingContainer: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    paymentContainer: {
        gap: 12,
    },
    paymentGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            ${xScreen("lte", "sm")} {
                padding-top: 6px;
                padding-bottom: 6px;
            }
        `,
    },
    poGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            ${xScreen("lte", "sm")} {
                padding-top: 6px;
                padding-bottom: 6px;
            }
        `,
    },
    jobGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            ${xScreen("lte", "sm")} {
                padding-top: 6px;
                padding-bottom: 6px;
            }
        `,
    },
};

export const checkoutReviewPickupSectionStyles: CheckoutReviewSectionStyles = {
    ...sharedReviewSectionStyles,
    container: {
        gap: 88,
        css: css`
            margin-bottom: 36px;
        `,
    },
    gridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            ${xScreen("lte", "sm")} {
                padding-top: 6px;
                padding-bottom: 6px;
            }
        `,
    },
    paymentContainer: {
        gap: 12,
    },
    paymentGridItem: {
        width: 12,
        css: css`
            ${xScreen("lte", "sm")} {
                padding-top: 6px;
                padding-bottom: 6px;
            }
        `,
    },
};

interface CheckoutReviewAddressDisplay {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    addressGridItem?: GridItemProps;
    locationNameText?: TypographyPresentationProps;
    address?: AddressInfoDisplayStyles;
}

export const checkoutReviewAddressDisplay: CheckoutReviewAddressDisplay = {
    container: { gap: 8 },
    headingGridItem: { width: [12, 12, 12, 5, 5] },
    headingText: {
        weight: "bold",
        css: css`
            &&& {
                line-height: 26px;
            }
        `,
    },
    addressGridItem: { width: [12, 12, 12, 7, 7] },
};
